import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { tenantApiFetch, getTenantToken } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';
import { apiFetch } from '../api';

const API_BASE = process.env.VIRGIL__API_URL || '';

function statusColor(s) {
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'error';
  if (s === 'running') return 'info';
  if (s === 'cancelled') return 'default';
  return 'warning';
}

export default function TenantExportJobDetailPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/export-jobs/${jobId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setJob(json.job);
    } catch (e) {
      setError(e.message);
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/export-jobs/${jobId}/start`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Start failed');
      setJob(json.job);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/export-jobs/${jobId}/cancel`, { method: 'POST', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Cancel failed');
      setJob(json.job);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setError(null);
    try {
      const token = getTenantToken();
      const res = await apiFetch(`${API_BASE}/api/tenant/export-jobs/${jobId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${jobId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !job) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!job) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Export job #{job.id}</Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={job.job_type} />
            <Chip size="small" label={job.status} color={statusColor(job.status)} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {job.status === 'completed' && (
            <Button variant="contained" onClick={download}>
              Download CSV
            </Button>
          )}
          <TenantCan permission="tenant.exports.manage">
            {job.status === 'queued' && (
              <Button variant="outlined" onClick={start} disabled={busy}>
                Run export
              </Button>
            )}
            {(job.status === 'queued' || job.status === 'running') && (
              <Button variant="outlined" color="warning" onClick={cancel} disabled={busy}>
                Cancel
              </Button>
            )}
          </TenantCan>
          <Button variant="outlined" component={Link} to="/tenant/export-jobs">
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Requested by', job.requested_by ? `${job.requested_by.email || ''} (${job.requested_by.type})` : '—')}
          {row('File location (storage key)', job.file_location || '—')}
          {row('Started', job.started_at ? String(job.started_at) : '—')}
          {row('Completed', job.completed_at ? String(job.completed_at) : '—')}
          {row('Failed', job.failed_at ? String(job.failed_at) : '—')}
          {row('Error', job.error_message || '—')}
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Filters (JSON)
        </Typography>
        <Box
          component="pre"
          sx={{ m: 0, mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.8rem', overflow: 'auto' }}
        >
          {JSON.stringify(job.filters_json || {}, null, 2)}
        </Box>
      </Paper>
    </Box>
  );
}
